import EventCheckBox from './EventCheckBox';
import CalendarComponent from './calendarComponent/CalendarComponent';
import CalendarTab from './CalendarTab';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EVENT_ADD_URL, EVENT_SEARCH_URL } from '@/constants/calendar';
import { isMobile } from 'react-device-detect';
import { FaPlus } from 'react-icons/fa';
import EventForm from './eventForm/EventForm';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LoadScript } from '@react-google-maps/api';
import CalendarTabMobile from './CalendarTabMobile';
import { Icon } from '@iconify/react';
import { useDateParams } from './calendarComponent/calendarComponentHooks';

const EVENT_BUTTONS = [
  { label: 'Search', path: EVENT_SEARCH_URL },
  { label: 'Add Event', path: EVENT_ADD_URL },
];

const EventCalendar = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [calendarType, setCalendarType] = useState('calendar');
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [calendarView, setCalendarView] = useState('month');
  const calendarRef = useRef(null);

  useEffect(() => {
    const viewParam = searchParams.get('view');
    if (isMobile && (viewParam === 'list' || viewParam === 'calendar')) {
      setCalendarType(viewParam);
    }
  }, [searchParams]);

  useEffect(() => {
    searchParams.set('view', calendarType);
    setSearchParams(searchParams);
  }, [calendarType])

  const { initialDate, handleDateChange } = useDateParams();

  const onBtnClick = path => {
    if (path === EVENT_ADD_URL && isMobile) {
      setShowAddEventModal(true);
    } else {
      navigate(path);
    }
  };

  const handleViewChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setCalendarView(event.target.value);
  };

  const handleCalendarRef = useCallback(ref => {
    calendarRef.current = ref;
  }, []);

  const handleDateChangeCallback = useCallback(
    dateInfo => {
      handleDateChange(dateInfo);
    },
    [handleDateChange]
  );

  const handlePrevMonth = () => {
    const newDate = new Date(initialDate);
    if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }

    const newYear = newDate.getFullYear();
    const newMonth = newDate.getMonth() + 1;
    searchParams.set('year', newYear.toString());
    searchParams.set('month', newMonth.toString());
    setSearchParams(searchParams);
  };

  const handleNextMonth = () => {
    const newDate = new Date(initialDate);
    if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }

    const newYear = newDate.getFullYear();
    const newMonth = newDate.getMonth() + 1;
    searchParams.set('year', newYear.toString());
    searchParams.set('month', newMonth.toString());
    setSearchParams(searchParams);
  };

  const formatCurrentDate = () => {
    const formatted = initialDate.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
    return formatted;
  };

  if (isMobile) {
    return (
      <section className='relative w-full h-full mx-auto pb-[10px] box-border flex flex-col gap-[0.3rem]'>
        <div className='p-[20px] w-full h-fit gap-[8px] flex justify-center items-center'>
          <p className='text-[#1C1C1C] !text-sm !font-[600]'>Calendar</p>
        </div>

        <div className='w-full h-full px-[20px] gap-[8px] flex flex-col'>
          <div className='w-full h-fit gap-[16px] flex justify-between items-center'>
            <CalendarTabMobile />
            <div className='flex flex-row items-center justify-between'>
              <EventCheckBox />
            </div>
          </div>

          <div
            className={`w-full h-full gap-[8px] flex flex-col justify-center items-center rounded-[4px] p-[8px] ${calendarType === 'list' ? 'border:none' : 'border border-[#a7a7a7]'}`}
          >
            {calendarType === 'calendar' && (
              <>
                <div className='w-full h-fit gap-[8px] py-[8px] flex flex-row justify-between align-center items-center'>
                  <div
                    onClick={handlePrevMonth}
                    className='w-[22px] h-[22px] bg-transparent !text-[#949494] rounded-[4px] cursor-pointer'
                  >
                    <Icon
                      icon='fe:arrow-left'
                      className='iconify w-[22px] h-[22px]'
                    />
                  </div>
                  <div className='flex w-fit h-fit items-center justify-center gap-[16px]'>
                    {/* <select 
                       value={calendarView}
                       onChange={handleViewChange}
                       className="gap-[2px] w-[88px] h-[32px] align-center items-center justify-center border border-[#5C5C5C] rounded-[4px] color-[#5C5C5C] text-[#000000]"
                     >
                       <option value="month">Month</option>
                       <option value="week">Week</option>
                     </select>                   */}
                    <p className='text-[#1C1C1C] !text-base font-[600]'>
                      {formatCurrentDate()}
                    </p>
                  </div>
                  <div
                    onClick={handleNextMonth}
                    className='w-[22px] h-[22px] bg-transparent !text-[#949494] rounded-[4px] cursor-pointer'
                  >
                    <Icon
                      icon='fe:arrow-right'
                      className='iconify w-[22px] h-[22px]'
                    />
                  </div>
                </div>
                <div className='w-full h-fit gap-[8px] flex flex-col justify-center items-center mb-[8px]'>
                  <section className='w-full flex justify-between items-center gap-4'>
                    {EVENT_BUTTONS.filter(btn => btn.label === 'Search').map(
                      (btn, index) => {
                        return (
                          <div
                            key={index}
                            onClick={() => onBtnClick(btn.path)}
                            className='w-full h-8 bg-[#4b4b4b] text-[#c6c6c6] rounded-[4px] px-[14px] flex justify-center items-center gap-2 !text-base cursor-pointer'
                          >
                            {btn.label}
                            {btn.label === 'Search' && (
                              <img
                                src='/svg/search-yellow.svg'
                                className='!w-[14px] !h-[14px] mt-[2px]'
                                alt='search'
                              />
                            )}
                          </div>
                        );
                      }
                    )}
                  </section>
                </div>
              </>
            )}

            <section className='relative w-full h-full calendar-section fc-header-toolbar-hidden fc-toolbar-title-hidden fc-toolbar-chunk-hidden'>
              <CalendarComponent
                type={calendarType}
                view={calendarView}
                currentDate={initialDate}
                onDateChange={handleDateChangeCallback}
                onCalendarRef={handleCalendarRef}
              />
            </section>
          </div>
          {calendarType === 'calendar' && (
            <div className='w-full h-fit gap-[8px] py-[20px] flex justify-center items-center'>
              <section className='w-full flex justify-between items-center gap-4'>
                {EVENT_BUTTONS.filter(btn => btn.label === 'Add Event').map(
                  (btn, index) => {
                    return (
                      <div
                        key={index}
                        onClick={() => onBtnClick(btn.path)}
                        className='w-full h-14 bg-[#fccb16] text-[#1c1c1c] rounded-[4px] px-[14px] flex justify-center items-center gap-2 !text-lg !font-semibold cursor-pointer'
                      >
                        {btn.label}
                        <FaPlus className='text-[#1C1C1C] !text-sm w-[14px] h-[14px] mt-[2px]' />
                      </div>
                    );
                  }
                )}
              </section>
            </div>
          )}
        </div>

        {showAddEventModal && (
          <div className='fixed top-0 left-0 right-0 bottom-0 bg-black/50 z-[1000] flex justify-center items-center'>
            <div className='bg-white rounded-lg max-w-[1200px] w-[95vw] max-h-[80vh] overflow-y-auto'>
              <LoadScript
                googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                language='en'
                libraries={['places']}
              >
                <EventForm onClose={() => setShowAddEventModal(false)} />
              </LoadScript>
            </div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className='relative w-4/5 h-full mx-auto py-16 box-border flex flex-col gap-[0.3rem]'>
      <header className='flex justify-between items-center mb-2'>
        <CalendarTab calendarType={calendarType} setCalendarType={setCalendarType} />

        <section className='w-[17rem] h-8 flex justify-end items-center gap-4'>
          {EVENT_BUTTONS.map((btn, index) => {
            return (
              <div
                key={index}
                onClick={() => onBtnClick(btn.path)}
                className='relative top-0 right-0 py-2 px-3 rounded-md flex whitespace-nowrap items-center gap-2 !text-sm md:!text-base font-medium bg-[#001326] text-white cursor-pointer hover:scale-95'
              >
                {btn.label}
                {btn.label === 'Search' ? (
                  <img
                    src='/svg/search-yellow.svg'
                    className='w-[1.3rem] h-[1.3rem]'
                    alt='search'
                  />
                ) : (
                  <FaPlus style={{ color: '#fccb16' }} />
                )}
              </div>
            );
          })}
        </section>
      </header>
      <div className='flex flex-row justify-between items-center'>
        <EventCheckBox />
      </div>

      <section className='relative h-full calendar-section'>
        <CalendarComponent
          type={calendarType}
          view={calendarView}
          currentDate={initialDate}
          onDateChange={handleDateChangeCallback}
          onCalendarRef={handleCalendarRef}
        />
      </section>
    </section>
  );
};

export default EventCalendar;
